import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import api from '../lib/api';

const AuthContext = createContext(null);

// Platform Owner email (for device trust check)
const OWNER_EMAIL = 'susannadavid.kelly.1234@gmail.com';

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const init = async () => {
      const token = localStorage.getItem('sd_token');
      const isOwnerDevice = localStorage.getItem('sd_owner_device') === 'true';

      // 1. Try existing token first
      if (token) {
        try {
          const res = await api.get('/auth/me');
          setUser(res.data);
          setLoading(false);
          return;
        } catch (err) {
          // Token invalid - clear it
          localStorage.removeItem('sd_token');
          // If owner device, try to get a new token
          if (isOwnerDevice) {
            try {
              const ownerRes = await api.get('/auth/owner-session');
              if (ownerRes.data.token) {
                localStorage.setItem('sd_token', ownerRes.data.token);
                setUser(ownerRes.data.user);
                setLoading(false);
                return;
              }
            } catch {
              // Owner session failed, clear flag
              localStorage.removeItem('sd_owner_device');
            }
          }
        }
      }

      // 2. Auto-login owner on trusted devices via secure backend endpoint
      if (isOwnerDevice && !token) {
        try {
          const res = await api.get('/auth/owner-session');
          if (res.data.token) {
            localStorage.setItem('sd_token', res.data.token);
            setUser(res.data.user);
            setLoading(false);
            return;
          }
        } catch {
          // Owner session not available, clear flag
          localStorage.removeItem('sd_owner_device');
        }
      }

      setLoading(false);
    };

    init();
  }, []);

  const login = useCallback(async (email, password) => {
    const res = await api.post('/auth/login', { email, password });
    localStorage.setItem('sd_token', res.data.token);
    setUser(res.data.user);
    
    // Mark as owner device if owner logs in
    if (email === OWNER_EMAIL) {
      localStorage.setItem('sd_owner_device', 'true');
    }
    
    return res.data;
  }, []);

  const signup = useCallback(async (email, username, password, display_name) => {
    const res = await api.post('/auth/signup', { email, username, password, display_name });
    localStorage.setItem('sd_token', res.data.token);
    setUser(res.data.user);
    return res.data;
  }, []);

  const logout = useCallback(() => {
    localStorage.removeItem('sd_token');
    // Keep owner device flag - owner can re-enter easily
    // Only clear if explicitly signing out as different user
    if (user?.email !== OWNER_EMAIL) {
      localStorage.removeItem('sd_owner_device');
    }
    setUser(null);
  }, [user]);

  const updateUser = useCallback((data) => setUser(prev => ({ ...prev, ...data })), []);

  // Quick owner entry function via secure backend endpoint
  const ownerQuickEntry = useCallback(async () => {
    try {
      const res = await api.get('/auth/owner-session');
      if (res.data.token) {
        localStorage.setItem('sd_token', res.data.token);
        localStorage.setItem('sd_owner_device', 'true');
        setUser(res.data.user);
        return res.data;
      }
      throw new Error('No token received');
    } catch (err) {
      throw new Error('Owner entry failed');
    }
  }, []);

  const token = typeof window !== 'undefined' ? localStorage.getItem('sd_token') : null;

  return (
    <AuthContext.Provider value={{ user, token, loading, login, signup, logout, updateUser, ownerQuickEntry }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
